import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { matchOptions } from './search-select-match.js';
import type { SelectOption } from './search-select-match.js';
import './search-select.css';

export type { SelectOption } from './search-select-match.js';

/**
 * A select you can type into (2026-10-02): "the pick a vendor should be a
 * dropdown that is searchable too, like how the register field is searchable."
 *
 * Shows the chosen name; press it or type, and the list narrows as you type —
 * ranked by `matchOptions`, scrolling when it is long. Arrow keys move, Enter
 * picks, Escape leaves the choice as it was. The ARIA combobox pattern, so a
 * screen reader and a browser journey both find it by its label.
 *
 * Used wherever a list is long enough that scrolling it is the slow part: the
 * 106 vendors first.
 */
export function SearchSelect({
  id,
  'aria-describedby': describedBy,
  'aria-label': ariaLabel,
  options,
  value,
  onChange,
  placeholder = 'Type to search',
  noneLabel,
  disabled = false,
  emptyText = 'Nothing matches',
  newLabel,
  maxLength,
}: {
  id?: string;
  'aria-describedby'?: string | undefined;
  /** For a picker with no visible label of its own (a customer's add-a-tag pill). */
  'aria-label'?: string;
  options: readonly SelectOption[];
  /** The chosen option's value; '' is nothing chosen. */
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** When set, the first row clears the choice and is called this. */
  noneLabel?: string;
  disabled?: boolean;
  emptyText?: string;
  /**
   * When set, what was typed can be picked even though it is not a choice yet —
   * a new gender group, a new customer tag — offered as the last row under
   * this label (2026-10-16: the browser's own suggestion list was replaced).
   */
  newLabel?: (typed: string) => string;
  maxLength?: number;
}) {
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  /** Null until something is typed: the box shows the choice, the list shows everything. */
  const [query, setQuery] = useState<string | null>(null);
  const [active, setActive] = useState(0);

  // A value that is not one of the options is one typed new, and shows as itself.
  const chosen =
    options.find((option) => option.value === value) ??
    (newLabel !== undefined && value !== '' ? { value, label: value } : null);
  const typed = (query ?? '').trim();
  const fresh: SelectOption[] =
    newLabel !== undefined &&
    typed !== '' &&
    !options.some((option) => option.label.toLowerCase() === typed.toLowerCase())
      ? [{ value: typed, label: newLabel(typed) }]
      : [];
  const none: SelectOption[] =
    noneLabel === undefined || (query !== null && query.trim() !== '')
      ? []
      : [{ value: '', label: noneLabel }];
  const shown = [...none, ...matchOptions(options, query ?? ''), ...fresh];

  // The highlighted row stays in view as the arrow keys walk a long list.
  useEffect(() => {
    if (!open) return;
    const row = listRef.current?.children[active] as HTMLElement | undefined;
    row?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  /*
   * The list floats in the top layer, under its box (above it when there is no
   * room below), and follows the box as the page or a dialog scrolls.
   *
   * It used to take its own room inside a dialog, so a dialog's scroll box
   * could not clip it — and so the dialog grew while it was open. Pressing
   * Cancel then closed the list on the press, the dialog shrank, and the
   * button moved out from under the pointer before the release: the press did
   * nothing (found 2026-10-16). Floating, it moves nothing beneath it.
   */
  useLayoutEffect(() => {
    if (!open) return;
    const list = listRef.current;
    if (list === null) return;
    if (typeof list.showPopover === 'function' && !list.matches(':popover-open')) {
      list.showPopover();
    }
    const place = () => {
      const box = inputRef.current?.getBoundingClientRect();
      if (box === undefined) return;
      const gutter = 8;
      const below = window.innerHeight - box.bottom - gutter - 4;
      const above = box.top - gutter - 4;
      const upward = below < 160 && above > below;
      const room = Math.max(120, upward ? above : below);
      // As wide as its box, and never so narrow a name wraps (a tag's small pill).
      const width = Math.min(Math.max(box.width, 224), window.innerWidth - 2 * gutter);
      list.style.width = `${width}px`;
      list.style.left = `${Math.max(gutter, Math.min(box.left, window.innerWidth - width - gutter))}px`;
      list.style.maxHeight = `${Math.min(288, room)}px`;
      list.style.top = upward ? 'auto' : `${box.bottom + 4}px`;
      list.style.bottom = upward ? `${window.innerHeight - box.top + 4}px` : 'auto';
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  const close = () => {
    setOpen(false);
    setQuery(null);
  };

  const pick = (option: SelectOption | undefined) => {
    if (option === undefined) return;
    onChange(option.value);
    close();
  };

  const openList = () => {
    if (disabled) return;
    setOpen(true);
    // Start on the current choice, so Enter without moving keeps it.
    const at = shown.findIndex((option) => option.value === value);
    setActive(at < 0 ? 0 : at);
  };

  return (
    <div className="search-select">
      <input
        ref={inputRef}
        id={id}
        aria-describedby={describedBy}
        aria-label={ariaLabel}
        className="input search-select__input"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={
          open && shown[active] !== undefined ? `${listId}-${active}` : undefined
        }
        autoComplete="off"
        spellCheck={false}
        maxLength={maxLength}
        disabled={disabled}
        placeholder={chosen === null ? placeholder : chosen.label}
        value={query ?? chosen?.label ?? ''}
        onFocus={(event) => {
          openList();
          event.target.select();
        }}
        onClick={openList}
        onBlur={close}
        onChange={(event) => {
          setQuery(event.target.value);
          setActive(0);
          setOpen(true);
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            if (!open) openList();
            else setActive((at) => Math.min(at + 1, shown.length - 1));
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActive((at) => Math.max(at - 1, 0));
          } else if (event.key === 'Enter') {
            if (open) {
              event.preventDefault();
              pick(shown[active]);
            }
          } else if (event.key === 'Escape') {
            if (open) {
              event.preventDefault();
              event.stopPropagation();
              close();
            }
          }
        }}
      />
      {open ? (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          className="search-select__list"
          popover="manual"
        >
          {shown.length === 0 ? (
            <li className="search-select__empty" role="presentation">
              {emptyText}
            </li>
          ) : (
            shown.map((option, index) => (
              <li
                // The new row can carry an existing option's value in another case.
                key={option.value === '' ? '-none-' : `${index}-${option.value}`}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={option.value === value}
                className={`search-select__option${index === active ? ' search-select__option--active' : ''}`}
                // Before the input's blur, or the list closes under the press.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => pick(option)}
                onMouseEnter={() => setActive(index)}
              >
                <span>{option.label}</span>
                {option.hint === undefined ? null : (
                  <span className="search-select__hint">{option.hint}</span>
                )}
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}
